import { Button } from '@/components/ui/button';
import { CardHeader, CardTitle } from '@/components/ui/card';
import { Wand2, Save } from 'lucide-react';
import { cn } from "@/lib/utils/general";

interface BuilderHeaderProps {
  initialBlueprint?: {
    name: string;
    description: string;
    version: string;
    theme: string;
    visibility: 'public' | 'private';
    collaborative: boolean;
  };
  isLoading: boolean;
  isGenerating?: boolean;
  isValid: boolean;
  hasResult: boolean;
  isSaved: boolean;
  onTest: () => void;
  onSave: () => void;
}

export function BuilderHeader({
  initialBlueprint,
  isLoading,
  isGenerating,
  isValid,
  hasResult,
  isSaved,
  onTest,
  onSave
}: BuilderHeaderProps) {
  return (
    <CardHeader>
      <CardTitle className="flex justify-between items-center">
        <div>
          {initialBlueprint ? (
            <>
              <h2 className="text-2xl font-bold">{initialBlueprint.name}</h2>
              <p className="text-sm text-muted-foreground mt-1">
                {initialBlueprint.description}
              </p>
              <div className="flex gap-2 mt-2">
                <span className="text-xs bg-blue-500/10 text-blue-500 dark:bg-blue-500/20 dark:text-blue-400 px-2 py-1 rounded">
                  v{initialBlueprint.version}
                </span>
                <span className="text-xs bg-purple-500/10 text-purple-500 dark:bg-purple-500/20 dark:text-purple-400 px-2 py-1 rounded">
                  {initialBlueprint.theme}
                </span>
                <span className={cn(
                  "text-xs px-2 py-1 rounded",
                  initialBlueprint.visibility === 'public' 
                    ? "bg-green-500/10 text-green-500 dark:bg-green-500/20 dark:text-green-400"
                    : "bg-gray-500/10 text-gray-500 dark:bg-gray-500/20 dark:text-gray-400"
                )}>
                  {initialBlueprint.visibility}
                </span>
                <span className={cn(
                  "text-xs px-2 py-1 rounded",
                  initialBlueprint.collaborative
                    ? "bg-amber-500/10 text-amber-500 dark:bg-amber-500/20 dark:text-amber-400"
                    : "bg-gray-500/10 text-gray-500 dark:bg-gray-500/20 dark:text-gray-400"
                )}>
                  {initialBlueprint.collaborative ? 'Collaborative' : 'Private'}
                </span>
              </div>
            </>
          ) : (
            <h2 className="text-2xl font-bold">Create New Blueprint</h2>
          )}
        </div>
        <div className="flex gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={onTest}
            disabled={isLoading || !isValid}
            className={cn(
              "h-8 w-8 border border-purple-500/20 rounded-full hover:bg-purple-500/10 hover:border-purple-500/40 transition-colors",
              (isLoading || isGenerating) && "animate-pulse-soft border-purple-500/40",
              !isValid && "opacity-50"
            )}
            title="Test Blueprint"
          >
            <Wand2 className="h-4 w-4 text-purple-500 dark:text-purple-400" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={onSave}
            disabled={!hasResult || !isValid}
            className={cn(
              "h-8 w-8 border border-green-500/20 rounded-full hover:bg-green-500/10 hover:border-green-500/40 transition-colors",
              !hasResult || !isValid && "opacity-50",
              isSaved && "bg-green-500/10 border-green-500/40"
            )}
            title={isSaved ? "Saved!" : "Save Blueprint"}
          >
            <Save className={cn(
              "h-4 w-4 text-green-500 dark:text-green-400",
              isSaved && "text-green-600 dark:text-green-500"
            )} />
          </Button>
        </div>
      </CardTitle>
    </CardHeader>
  );
} 